import { create } from 'zustand';
import type { BulkReleasedItem, BulkUndoItem } from '@/types/approval';

/** Length of the undo window offered when the response does not carry the server window. */
export const DEFAULT_UNDO_WINDOW_MS = 10_000;

export interface ReleaseUndoEntry {
  /** Unique per announcement so the countdown restarts for a new release */
  token: number;
  postId: number;
  /** null = the Original */
  variationId: number | null;
  /** Epoch ms at which the undo window closes */
  undoUntilMs: number;
  /** Set for a bulk release: every released item (postId/variationId above are then the first one) */
  bulkItems?: BulkUndoItem[];
  /** Bulk release with skipped items: full summary text ("8 released · 2 skipped") shown instead of the count */
  summary?: string;
  /** Opens the skipped-items details (shown as a "Details" button next to Undo) */
  onDetails?: () => void;
}

interface ReleaseUndoStore {
  entry: ReleaseUndoEntry | null;
  show: (input: Omit<ReleaseUndoEntry, 'token'>) => void;
  dismiss: () => void;
}

let nextToken = 1;

/**
 * Global "Released · Undo" snackbar state. Only the person who released sees it
 * (it is only ever shown from the release click paths). One entry at a time:
 * a newer release replaces the previous undo offer.
 */
export const useReleaseUndoStore = create<ReleaseUndoStore>((set) => ({
  entry: null,
  show: (input) => set({ entry: { ...input, token: nextToken++ } }),
  dismiss: () => set({ entry: null }),
}));

/**
 * Show the undo snackbar after a successful release.
 *
 * The window is measured on the SERVER clock (undoUntil - reviewedAt, both from the response) and then
 * counted down from the moment the response arrived, so a wrong client clock can neither hide the offer
 * nor extend it. Without those two timestamps it is a fixed 10 s from receipt.
 */
const serverWindowMs = (undoUntil?: string, reviewedAt?: string | null): number => {
  const until = undoUntil ? new Date(undoUntil).getTime() : Number.NaN;
  const reviewed = reviewedAt ? new Date(reviewedAt).getTime() : Number.NaN;
  const serverWindow = until - reviewed;
  return Number.isFinite(serverWindow) && serverWindow > 0 && serverWindow <= 60_000
    ? serverWindow
    : DEFAULT_UNDO_WINDOW_MS;
};

/**
 * Show ONE undo snackbar for a bulk release ("N released · Undo"). Items are released one after another,
 * so the offer is based on the REMAINING time of the earliest one, measured on the SERVER clock:
 * window = min(undoUntil) - max(reviewedAt), counted down from receipt. Nothing is shown when that is
 * <= 0 (the first items are already past their window), nor for items the server flagged as final
 * (`undoAvailable === false`, release effects already ran). Returns whether the snackbar was shown
 * (false = the caller shows its own summary toast).
 */
export const announceBulkRelease = (
  released: Array<BulkReleasedItem & { undoAvailable?: boolean }>,
  extras?: { summary?: string; onDetails?: () => void }
): boolean => {
  const offerable = released.filter((r) => r.undoAvailable !== false);
  if (offerable.length === 0) return false;
  const untilMs = offerable.map((r) => new Date(r.undoUntil).getTime());
  const reviewedMs = offerable.map((r) => new Date(r.reviewedAt).getTime());
  const windowMs = Math.min(...untilMs) - Math.max(...reviewedMs);
  if (!Number.isFinite(windowMs) || windowMs <= 0) return false;
  useReleaseUndoStore.getState().show({
    postId: offerable[0].postId,
    variationId: offerable[0].variationId,
    undoUntilMs: Date.now() + Math.min(windowMs, DEFAULT_UNDO_WINDOW_MS),
    bulkItems: offerable.map((r) => ({ postId: r.postId, variationId: r.variationId })),
    summary: extras?.summary,
    onDetails: extras?.onDetails,
  });
  return true;
};

export const announceRelease = (input: {
  postId: number;
  variationId: number | null;
  undoUntil?: string;
  reviewedAt?: string | null;
  /** false = the server already ran the release effects: the release is final, no undo offer */
  undoAvailable?: boolean;
}): void => {
  if (input.undoAvailable === false) return;
  const windowMs = serverWindowMs(input.undoUntil, input.reviewedAt);
  useReleaseUndoStore.getState().show({
    postId: input.postId,
    variationId: input.variationId,
    undoUntilMs: Date.now() + windowMs,
  });
};
